import { useSponsorsGetKpisQuery } from "@/lib/api/requests/sponsors.requests";
import { Skeleton } from "@/ui-components/ui/skeleton";
import { Building2, Coins, Handshake, Heart, Target, TrendingUp, TrendingDown, Minus } from "lucide-react";

const SponsorKPIs = () => {
  const { data: sponsorKPIs, isLoading } = useSponsorsGetKpisQuery();

  if (isLoading || !sponsorKPIs) {
    return <SponsorKPIsSkeleton />;
  }

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency || "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat("de-DE").format(num);
  };

  const kpis = [
    {
      title: "Aktive Sponsoren",
      value: formatNumber(sponsorKPIs.activeSponsorsCount),
      icon: Handshake,
      trend: sponsorKPIs.activeSponsorsCountTrend,
    },
    {
      title: "Gesamtvolumen",
      value: formatCurrency(sponsorKPIs.totalVolume, sponsorKPIs.currency || "EUR"),
      icon: Coins,
      trend: sponsorKPIs.totalVolumeTrend,
    },
    {
      title: "Ø Volumen",
      value: formatCurrency(sponsorKPIs.averageVolume, sponsorKPIs.currency || "EUR"),
      icon: TrendingUp,
      trend: sponsorKPIs.averageVolumeTrend,
    },
    {
      title: "Ø Spenden",
      value: formatCurrency(sponsorKPIs.averageVolumeDonations, sponsorKPIs.currency || "EUR"),
      icon: Heart,
      trend: sponsorKPIs.averageVolumeDonationsTrend,
    },
    {
      title: "Ø Sponsoring",
      value: formatCurrency(sponsorKPIs.averageVolumeSponsoring, sponsorKPIs.currency || "EUR"),
      icon: Target,
      trend: sponsorKPIs.averageVolumeSponsoringTrend,
    },
    {
      title: "Ø Mandanten",
      value: formatCurrency(sponsorKPIs.averageVolumeMandants, sponsorKPIs.currency || "EUR"),
      icon: Building2,
      trend: sponsorKPIs.averageVolumeMandantsTrend,
    },
  ];

  const getTrendDisplay = (trend?: number) => {
    if (trend === undefined || trend === null) return null;

    let colorClass = "text-gray-400";
    let Icon = Minus;
    let prefix = "";

    if (trend > 0) {
      colorClass = "text-green-600";
      Icon = TrendingUp;
      prefix = "+";
    } else if (trend < 0) {
      colorClass = "text-red-600";
      Icon = TrendingDown;
    }

    return (
      <div className={`flex items-center text-xs font-bold ${colorClass} mb-1 ml-2`}>
        <Icon className="w-3 h-3 mr-1" />
        {prefix}
        {trend}%
      </div>
    );
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-6 p-6">
      {kpis.map((kpi, idx) => (
        <div key={idx} className="flex flex-col gap-1">
          <div className="flex items-end">
            <div className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">{kpi.value}</div>
            {getTrendDisplay(kpi.trend)}
          </div>
          <div className="text-sm font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
            <span className="opacity-70 text-gray-400">
              <kpi.icon className="w-4 h-4" />
            </span>
            {kpi.title}
          </div>
        </div>
      ))}
    </div>
  );
};

const SponsorKPIsSkeleton = () => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-6 p-6">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="flex flex-col gap-2">
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-4 w-32" />
        </div>
      ))}
    </div>
  );
};

export default SponsorKPIs;
